
export const AUDIENCE_SECTIONS = [
  { key: "departments", label: "Department" },
  { key: "designations", label: "Designation" },
  { key: "workLocations", label: "Location" },
];

export const flattenAudiences = (data) => {
  if (!data) return [];
  return AUDIENCE_SECTIONS.reduce((all, section) => {
    const rows = Array.isArray(data[section.key]) ? data[section.key] : [];
    return all.concat(
      rows.map((row) => ({
        id: row.id,
        name: row.name,
        audienceType: section.key,
        sectionLabel: section.label,
      }))
    );
  }, []);
};

export const findAudience = (data, audienceId) => {
  if (!audienceId) return null;
  return flattenAudiences(data).find((item) => item.id === audienceId) || null;
};

export const MESSAGE_MAX_LENGTH = 2000;

export const VALUE_CHIP_CLASS =
  "inline-flex items-center rounded-full bg-blue-secondery-1 px-3 py-1 text-xs font-semibold text-blue-700";

export const AUDIENCE_CHIP_CLASS =
  "inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600";

export const normalizeKudos = (raw) => {
  if (!raw) return null;
  return {
    id: raw.id || raw._id || null,
    type: raw.type || "KUDOS",
    authorId: raw.authorId || null,
    authorName: raw.authorName || "",
    recipientId: raw.recipientId || null,
    recipientName: raw.recipientName || "",
    // The human-readable code (ICS-98999), not the mongo id.
    recipientCode: raw.recipientEmployeeId || "",
    message: raw.message || "",
    values: Array.isArray(raw.recognizeValueDetails)
      ? raw.recognizeValueDetails
      : [],
    audienceType: raw.audienceType || null,
    audienceId: raw.audienceId || null,
    audienceName: raw.audienceName || "",
    createdAt: raw.createdAt || null,
    canDelete: raw.canDelete === true,
    reactions: Array.isArray(raw.reactions) ? raw.reactions : [],
  };
};

export const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

export const parseServerDate = (value) => {
  if (!value) return null;
  const trimmed = String(value).replace(/(\.\d{3})\d+$/, "$1");
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const timeAgo = (value) => {
  const date = parseServerDate(value);
  if (!date) return "";
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + " min ago";
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + " hr ago";
  const days = Math.floor(hours / 24);
  if (days < 30) return days === 1 ? "1 day ago" : days + " days ago";
  return date.toLocaleDateString();
};

export const POLL_LIMITS = {
  MIN_OPTIONS: 2,
  MAX_OPTIONS: 10,
  QUESTION_MAX: 300,
  MIN_DAYS: 1,
  MAX_DAYS: 30,
};

const normalizePostBase = (raw) => ({
  id: raw.id || raw._id || null,
  type: raw.type || null,
  authorId: raw.authorId || null,
  authorName: raw.authorName || "",
  audienceType: raw.audienceType || null,
  audienceId: raw.audienceId || null,
  audienceName: raw.audienceName || "",
  createdAt: raw.createdAt || null,
  canDelete: raw.canDelete === true,
  reactions: Array.isArray(raw.reactions) ? raw.reactions : [],
});

export const normalizePoll = (raw) => {
  if (!raw) return null;
  const poll = raw.poll || {};
  return {
    ...normalizePostBase(raw),
    type: raw.type || "POLL",
    poll: {
      question: poll.question || "",
      options: (Array.isArray(poll.options) ? poll.options : []).map(
        (option) => ({
          optionId: option.optionId,
          text: option.text || "",
          votes: typeof option.votes === "number" ? option.votes : 0,
        })
      ),

      anonymous: poll.anonymous === true,
      expiresAt: poll.expiresAt || null,
      closed: poll.closed === true,
      totalVotes: typeof poll.totalVotes === "number" ? poll.totalVotes : 0,

      myVote: poll.myVote == null ? null : poll.myVote,
    },
  };
};

export const normalizePost = (raw) => {
  if (!raw) return null;
  if (raw.type === POST_TYPE.POLL) return normalizePoll(raw);
  if (raw.type === POST_TYPE.ANNOUNCEMENT) return normalizeAnnouncement(raw);
  if (isCelebration(raw.type)) return normalizeCelebration(raw);
  return normalizeKudos(raw);
};

export const mergePollVote = (current, updated) => {
  if (!current) return current;
  if (!updated) return current;
  const votesById = {};
  (Array.isArray(updated.options) ? updated.options : []).forEach((option) => {
    votesById[option.optionId] = option.votes;
  });
  return {
    ...current,
    options: (current.options || []).map((option) => ({
      ...option,
      votes:
        votesById[option.optionId] == null
          ? option.votes
          : votesById[option.optionId],
    })),
    totalVotes:
      updated.totalVotes == null ? current.totalVotes : updated.totalVotes,
    myVote: updated.myVote === undefined ? current.myVote : updated.myVote,
    closed: updated.closed == null ? current.closed : updated.closed,
  };
};

export const pollPercent = (votes, totalVotes) => {
  if (!totalVotes || totalVotes <= 0) return 0;
  return Math.round((votes / totalVotes) * 100);
};

export const formatPollDeadline = (expiresAt, closed) => {
  if (closed) return "Closed";
  const date = parseServerDate(expiresAt);
  if (!date) return "";
  const ms = date.getTime() - Date.now();
  if (ms <= 0) return "Closing";

  if (ms < 86400000) {
    const hours = Math.max(1, Math.ceil(ms / 3600000));
    return hours === 1 ? "Closes in 1 hour" : `Closes in ${hours} hours`;
  }
  const days = Math.round(ms / 86400000);
  return days === 1 ? "Closes in 1 day" : `Closes in ${days} days`;
};

export const POLL_BADGE_CLASS =
  "inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-teal-50 text-teal-700";

export const KUDOS_BADGE_CLASS =
  "inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-pink-50 text-pink-700";

export const ANON_BADGE_CLASS =
  "inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600";

// Celebrations are TWO types, not one "CELEBRATION" - the birthday variant
// carries anniversaryYears: null.
export const POST_TYPE = {
  KUDOS: "KUDOS",
  POLL: "POLL",
  ANNOUNCEMENT: "ANNOUNCEMENT",
  BIRTHDAY: "BIRTHDAY",
  WORK_ANNIVERSARY: "WORK_ANNIVERSARY",
};

export const isCelebration = (type) =>
  type === POST_TYPE.BIRTHDAY || type === POST_TYPE.WORK_ANNIVERSARY;

// Celebrations have no author and no audience - they are derived, not posted.
export const normalizeCelebration = (raw) => {
  if (!raw) return null;
  return {
    ...normalizePostBase(raw),
    type: raw.type,
    employeeId: raw.employeeId || null,
    employeeName: raw.employeeName || "",
    // 0 is a colleague in their first year; only null means not applicable.
    anniversaryYears: raw.anniversaryYears == null ? null : raw.anniversaryYears,
  };
};

export const celebrationHeading = (type) =>
  type === POST_TYPE.BIRTHDAY ? "Happy Birthday!" : "Congratulations!";

export const celebrationKindLabel = (type) =>
  type === POST_TYPE.BIRTHDAY ? "Birthday" : "Work Anniversary";

export const anniversaryLine = (years) => {
  if (years == null) return "";
  return years === 1 ? "1 year with us" : `${years} years with us`;
};

export const REACTION_SET = [
  "\u{1F389}",
  "\u{1F929}",
  "\u{1F680}",
  "\u{1F44F}",
  "\u{1F4AF}",
  "❤️",
];

// Reaction URLs are namespaced by post kind. Polls have no react endpoint, so
// this returns null and the card renders no reaction bar.
export const reactionSegment = (type) => {
  if (type === POST_TYPE.KUDOS) return "kudos";
  if (type === POST_TYPE.ANNOUNCEMENT) return "announcement";
  if (isCelebration(type)) return "celebration";
  return null;
};

export const canReact = (type) => reactionSegment(type) !== null;

export const normalizeReactions = (list) =>
  (Array.isArray(list) ? list : []).map((row) => ({
    emoji: row.emoji,
    count: typeof row.count === "number" ? row.count : 0,
    reactedByMe: row.reactedByMe === true,
  }));

export const normalizeReactionGroups = (list) =>
  (Array.isArray(list) ? list : []).map((row) => ({
    emoji: row.emoji,
    count: typeof row.count === "number" ? row.count : 0,
    reactedByMe: row.reactedByMe === true,
    reactors: (Array.isArray(row.reactors) ? row.reactors : []).map(
      (person) => ({
        employeeId: person.employeeId || null,
        name: person.name || "",
      })
    ),
  }));

export const myReactionEmoji = (reactions) =>
  normalizeReactions(reactions)
    .filter((row) => row.reactedByMe)
    .map((row) => row.emoji);

// The API replaces the caller's whole set with whatever is sent, so toggling
// one emoji means sending every other emoji of mine alongside it.
export const nextEmojiSet = (reactions, emoji) => {
  const mine = myReactionEmoji(reactions);
  return mine.indexOf(emoji) === -1
    ? mine.concat([emoji])
    : mine.filter((item) => item !== emoji);
};

// The same toggle applied locally so the pill moves before the request
// settles; replaced by the server's array on response, rolled back on failure.
export const previewReactions = (reactions, emoji) => {
  const list = normalizeReactions(reactions).map((row) => ({ ...row }));
  const found = list.find((row) => row.emoji === emoji);
  if (!found) {
    list.push({ emoji, count: 1, reactedByMe: true });
  } else if (found.reactedByMe) {
    found.count -= 1;
    found.reactedByMe = false;
  } else {
    found.count += 1;
    found.reactedByMe = true;
  }
  return list.filter((row) => row.count > 0);
};

export const totalReactionCount = (reactions) =>
  normalizeReactions(reactions).reduce((sum, row) => sum + row.count, 0);

// Wire values with our labels. "ALL" and "" are sentinels the api layer drops.
export const DEFAULT_FEED_FILTERS = {
  sort: "NEWEST",
  activity: "ALL",
  audienceId: "",
};

export const SORT_OPTIONS = [
  { value: "NEWEST", label: "Newest post" },
  { value: "OLDEST", label: "Oldest post" },
];

export const ACTIVITY_OPTIONS = [
  { value: "ALL", label: "All activities" },
  { value: POST_TYPE.KUDOS, label: "Kudos" },
  { value: POST_TYPE.POLL, label: "Polls" },
  { value: POST_TYPE.ANNOUNCEMENT, label: "Announcements" },
  { value: POST_TYPE.BIRTHDAY, label: "Birthdays" },
  { value: POST_TYPE.WORK_ANNIVERSARY, label: "Work anniversaries" },
];

export const hasActiveFilters = (filters) =>
  Boolean(filters) &&
  (filters.sort !== DEFAULT_FEED_FILTERS.sort ||
    filters.activity !== DEFAULT_FEED_FILTERS.activity ||
    Boolean(filters.audienceId));

// Grouped for an <optgroup> select, empty sections dropped so there are no
// blank headings.
export const audienceSections = (data) =>
  AUDIENCE_SECTIONS.map((section) => ({
    key: section.key,
    label: section.label,
    rows: Array.isArray(data?.[section.key]) ? data[section.key] : [],
  })).filter((section) => section.rows.length > 0);

// No priority field: the API takes title, body, audience and an optional image.
export const normalizeAnnouncement = (raw) => {
  if (!raw) return null;
  return {
    ...normalizePostBase(raw),
    type: raw.type || POST_TYPE.ANNOUNCEMENT,
    title: raw.title || "",
    body: raw.body || "",
    // The whole s3_Upload() wrapper, sent and echoed under this key.
    image: raw.image || null,
  };
};

export const ANNOUNCEMENT_BADGE_CLASS =
  "inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700";

export const imageDirectUrl = (image) => (image?.resourceName || "").trim();
export const imageObjectKey = (image) => {
  if (!image || typeof image !== "object") return "";
  if ("s3Document" in image) return image.s3Document?.fileName || "";
  return image.fileName || "";
};

export const hasImage = (image) =>
  Boolean(imageDirectUrl(image) || imageObjectKey(image));
export const isRestrictedAudience = (type) => type === POST_TYPE.POLL;

export const RESTRICTED_CHIP_CLASS =
  "inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700";

export const audienceChipLabel = (post) => {
  const name = post?.audienceName || "";
  if (!name) return "";
  return isRestrictedAudience(post?.type) ? `Only ${name}` : name;
};

export const audienceChipTitle = (post) =>
  isRestrictedAudience(post?.type)
    ? "Only this group can see and vote on this poll"
    : "Tagged group - everyone can see this post";

export const parseRoles = (raw) =>
  String(raw || "")
    .toUpperCase()
    .split(/[^A-Z_]+/)
    .map((role) => role.replace(/^ROLE_/, ""))
    .filter(Boolean);

export const hasRole = (raw, roles) => {
  const mine = parseRoles(raw);
  return roles.some((role) => mine.indexOf(role) !== -1);
};

// Announcements: ADMIN and PROXY_ADMIN compose them.
export const canComposeAnnouncement = (raw) =>
  hasRole(raw, ["ADMIN", "PROXY_ADMIN"]);

export const isAdministrativeRole = (raw) =>
  hasRole(raw, ["ADMIN", "PROXY_ADMIN"]);

// They read reactions and poll results but cast neither.
export const canGiveReactions = (raw) => !isAdministrativeRole(raw);

export const canVoteInPolls = (raw) => !isAdministrativeRole(raw);

// One line per post type for the compact dashboard tile. Deliberately never
// names a poll's author, so an anonymous poll stays anonymous here too.
export const highlightSummary = (post) => {
  if (!post) return { glyph: "", text: "" };
  switch (post.type) {
    case POST_TYPE.KUDOS:
      return {
        glyph: "\u{1F396}",
        text: `${post.authorName} gave kudos to ${post.recipientName}`,
      };
    case POST_TYPE.POLL:
      return { glyph: "\u{1F4CA}", text: post.poll?.question || "New poll" };
    case POST_TYPE.ANNOUNCEMENT:
      return { glyph: "\u{1F4E3}", text: post.title || post.body };
    case POST_TYPE.BIRTHDAY:
      return { glyph: "\u{1F382}", text: `${post.employeeName}'s birthday` };
    case POST_TYPE.WORK_ANNIVERSARY:
      return {
        glyph: "\u{1F389}",
        text:
          post.anniversaryYears == null
            ? `${post.employeeName} - work anniversary`
            : `${post.employeeName} - ${anniversaryLine(post.anniversaryYears)}`,
      };
    default:
      return { glyph: "", text: "" };
  }
};
